import { getCompanies, getDutyRequestStats, queryKeys } from "@dutyon/apis";
import {
  ActionIcon,
  Badge,
  Card,
  Grid,
  Group,
  Loader,
  LoadingOverlay,
  Paper,
  Progress,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  TitleContainer,
  Tooltip,
} from "@dutyon/ui-kit";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  Building2,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Clock,
  RefreshCw,
  TrendingUp,
  Zap,
} from "lucide-react";
import { useMemo, useState } from "react";

function formatMinutes(minutes: number): string {
  if (!minutes || minutes <= 0) return "-";
  if (minutes < 60) return `${minutes}분`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}시간 ${remainingMinutes}분` : `${hours}시간`;
}

export function DutyRequestStatisticsPage() {
  const [periodKey, setPeriodKey] = useState<string>("30d");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  // 병원 목록 조회 (셀렉트 옵션용)
  const { data: companiesData } = useQuery({
    queryKey: queryKeys.companies.all,
    queryFn: () => getCompanies(),
  });

  const companyOptions = useMemo(() => {
    const defaultOption = [{ value: "all", label: "전체 병원" }];
    if (!companiesData) return defaultOption;
    return [
      ...defaultOption,
      ...companiesData.map((comp) => ({
        value: comp.id,
        label: `${comp.name} (${comp.code})`,
      })),
    ];
  }, [companiesData]);

  const queryParams = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    let startDate = dayjs().subtract(29, "day").format("YYYY-MM-DD");

    switch (periodKey) {
      case "7d":
        startDate = dayjs().subtract(6, "day").format("YYYY-MM-DD");
        break;
      case "month":
        startDate = dayjs().startOf("month").format("YYYY-MM-DD");
        break;
      case "90d":
        startDate = dayjs().subtract(89, "day").format("YYYY-MM-DD");
        break;
      case "1y":
        startDate = dayjs().subtract(364, "day").format("YYYY-MM-DD");
        break;
      default:
        startDate = dayjs().subtract(29, "day").format("YYYY-MM-DD");
        break;
    }

    return {
      startDate,
      endDate: today,
      companyId: selectedCompanyId && selectedCompanyId !== "all" ? selectedCompanyId : undefined,
    };
  }, [periodKey, selectedCompanyId]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: queryKeys.statistics.dutyRequests(queryParams),
    queryFn: () => getDutyRequestStats(queryParams),
  });

  const maxDailyRequests = useMemo(() => {
    if (!data?.timeline?.length) return 1;
    return Math.max(...data.timeline.map((item) => item.totalRequests), 1);
  }, [data?.timeline]);

  return (
    <TitleContainer
      size="xl"
      title={
        <Group justify="space-between" align="center" wrap="wrap" style={{ width: "100%" }}>
          <Group gap="sm">
            <ThemeIcon size="lg" radius="md" variant="light" color="indigo">
              <ClipboardCheck size={22} />
            </ThemeIcon>
            <Stack gap={2}>
              <Title order={3}>Duty 생성 요청 및 응답률 통계</Title>
              <Text size="xs" c="dimmed">
                근무표 생성 요청의 접수 현황과 관리자의 응답 완료율, 평균 소요 시간을 분석합니다.
              </Text>
            </Stack>
          </Group>
          <Group gap="xs">
            <Select
              placeholder="병원 선택"
              data={companyOptions}
              value={selectedCompanyId || "all"}
              onChange={(val) => setSelectedCompanyId(val === "all" ? null : val)}
              size="xs"
              style={{ width: 180 }}
              allowDeselect={false}
              leftSection={<Building2 size={13} />}
            />
            <SegmentedControl
              value={periodKey}
              onChange={setPeriodKey}
              size="xs"
              radius="md"
              data={[
                { label: "최근 7일", value: "7d" },
                { label: "최근 30일", value: "30d" },
                { label: "이번 달", value: "month" },
                { label: "최근 90일", value: "90d" },
                { label: "1년", value: "1y" },
              ]}
            />
            <Tooltip label="새로고침">
              <ActionIcon
                variant="default"
                size="input-xs"
                radius="md"
                onClick={() => refetch()}
                loading={isFetching}
              >
                <RefreshCw size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      }
    >
      <Stack gap="lg" style={{ position: "relative" }}>
        <LoadingOverlay visible={isLoading || isFetching} overlayProps={{ blur: 1 }} />

        {/* 요약 KPI 카드 4종 */}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder radius="md" p="lg">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="xs" c="dimmed" fw={700}>
                    총 생성 요청
                  </Text>
                  <Title order={2} c="blue">
                    {(data?.summary.totalRequests ?? 0).toLocaleString()}
                    <Text span size="sm" c="dimmed" fw={500} ml={4}>
                      건
                    </Text>
                  </Title>
                </Stack>
                <ThemeIcon size={44} radius="md" color="blue" variant="light">
                  <ClipboardList size={22} />
                </ThemeIcon>
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                해당 기간 접수된 모든 근무 생성 요청
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder radius="md" p="lg">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="xs" c="dimmed" fw={700}>
                    응답 완료 건수
                  </Text>
                  <Title order={2} c="teal">
                    {(data?.summary.completedRequests ?? 0).toLocaleString()}
                    <Text span size="sm" c="dimmed" fw={500} ml={4}>
                      건
                    </Text>
                  </Title>
                </Stack>
                <ThemeIcon size={44} radius="md" color="teal" variant="light">
                  <CheckCircle2 size={22} />
                </ThemeIcon>
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                관리자가 근무표를 완료/응답한 건수
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder radius="md" p="lg">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="xs" c="dimmed" fw={700}>
                    처리 대기 중
                  </Text>
                  <Title order={2} c="yellow.8">
                    {(data?.summary.pendingRequests ?? 0).toLocaleString()}
                    <Text span size="sm" c="dimmed" fw={500} ml={4}>
                      건
                    </Text>
                  </Title>
                </Stack>
                <ThemeIcon size={44} radius="md" color="yellow" variant="light">
                  <Clock size={22} />
                </ThemeIcon>
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                아직 완료되지 않은 대기 상태 요청
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder radius="md" p="lg">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="xs" c="dimmed" fw={700}>
                    전체 응답률
                  </Text>
                  <Title order={2} c="indigo">
                    {data?.summary.responseRate ?? 0}%
                  </Title>
                </Stack>
                <ThemeIcon size={44} radius="md" color="indigo" variant="light">
                  <TrendingUp size={22} />
                </ThemeIcon>
              </Group>
              <Group justify="space-between" mt="xs" align="center">
                <Text size="xs" c="dimmed">
                  평균 처리 소요 시간
                </Text>
                <Badge size="xs" variant="light" color="indigo">
                  {formatMinutes(data?.summary.avgResponseTimeMinutes ?? 0)}
                </Badge>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* 시간순 추이 차트 */}
        <Card withBorder radius="md" p="lg">
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <Zap size={18} />
              <Title order={4}>일자별 요청 접수 및 응답 완료 추이</Title>
            </Group>
            <Group gap="sm">
              <Group gap={6}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    backgroundColor: "var(--mantine-color-blue-6)",
                    borderRadius: 2,
                  }}
                />
                <Text size="xs" c="dimmed">
                  요청 접수
                </Text>
              </Group>
              <Group gap={6}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    backgroundColor: "var(--mantine-color-teal-6)",
                    borderRadius: 2,
                  }}
                />
                <Text size="xs" c="dimmed">
                  응답 완료
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                ({queryParams.startDate} ~ {queryParams.endDate})
              </Text>
            </Group>
          </Group>

          {data?.timeline && data.timeline.length > 0 ? (
            <Stack gap="xs" mt="md">
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 6,
                  height: 200,
                  paddingBottom: 24,
                  overflowX: "auto",
                  borderBottom: "1px solid var(--mantine-color-gray-3)",
                }}
              >
                {data.timeline.map((day) => {
                  const totalHeight =
                    day.totalRequests > 0
                      ? Math.max(Math.round((day.totalRequests / maxDailyRequests) * 100), 8)
                      : 2;

                  const completedRatio =
                    day.totalRequests > 0 ? day.completedRequests / day.totalRequests : 0;
                  const completedHeight = Math.round(totalHeight * completedRatio);

                  return (
                    <Tooltip
                      key={day.date}
                      label={
                        <Stack gap={2} p={2}>
                          <Text fw={700} size="xs">
                            {day.date}
                          </Text>
                          <Text size="xs">요청 접수: {day.totalRequests}건</Text>
                          <Text size="xs" c="teal.4">
                            응답 완료: {day.completedRequests}건
                          </Text>
                          <Text size="xs" c="yellow.4">
                            대기 중: {day.pendingRequests}건
                          </Text>
                          <Text size="xs" fw={700}>
                            응답률: {day.responseRate}%
                          </Text>
                        </Stack>
                      }
                      withArrow
                    >
                      <div
                        style={{
                          flex: 1,
                          minWidth: 18,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          height: "100%",
                          justifyContent: "flex-end",
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            maxWidth: 24,
                            height: `${totalHeight}%`,
                            backgroundColor:
                              day.totalRequests > 0
                                ? "var(--mantine-color-blue-2)"
                                : "var(--mantine-color-gray-2)",
                            borderRadius: "4px 4px 0 0",
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-end",
                            overflow: "hidden",
                            transition: "height 0.3s ease",
                          }}
                        >
                          {completedHeight > 0 && (
                            <div
                              style={{
                                width: "100%",
                                height: `${(day.completedRequests / day.totalRequests) * 100}%`,
                                backgroundColor: "var(--mantine-color-teal-6)",
                                transition: "height 0.3s ease",
                              }}
                            />
                          )}
                        </div>
                        <Text
                          size="9px"
                          c="dimmed"
                          style={{
                            marginTop: 4,
                            whiteSpace: "nowrap",
                            transform: data.timeline.length > 15 ? "rotate(-45deg)" : "none",
                            transformOrigin: "center top",
                          }}
                        >
                          {day.date.slice(5)}
                        </Text>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </Stack>
          ) : (
            <Paper p="xl" withBorder radius="md" style={{ textAlign: "center" }}>
              <Text c="dimmed" size="sm">
                선택한 기간 동안의 생성 요청 내역이 없습니다.
              </Text>
            </Paper>
          )}
        </Card>

        {/* 병원별 요청 및 응답률 현황 테이블 */}
        <Card withBorder radius="md" p="lg">
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <Building2 size={18} />
              <Title order={4}>병원별 Duty 생성 요청 및 응답률 현황</Title>
            </Group>
            <Badge variant="light" color="indigo">
              총 {data?.companies.length ?? 0}개 병원 참여
            </Badge>
          </Group>

          {isLoading ? (
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 60 }}>순위</Table.Th>
                  <Table.Th>병원명 (코드)</Table.Th>
                  <Table.Th style={{ width: 120 }}>총 요청</Table.Th>
                  <Table.Th style={{ width: 120 }}>완료 건수</Table.Th>
                  <Table.Th style={{ width: 120 }}>대기 건수</Table.Th>
                  <Table.Th style={{ width: 220 }}>응답률</Table.Th>
                  <Table.Th style={{ width: 140 }}>평균 소요 시간</Table.Th>
                  <Table.Th style={{ width: 130 }}>최근 요청일</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td colSpan={8} align="center" py="xl">
                    <Stack align="center" gap="xs">
                      <Loader size="sm" />
                      <Text size="sm" c="dimmed">
                        병원별 요청 통계를 불러오는 중입니다...
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          ) : data?.companies && data.companies.length > 0 ? (
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 60 }}>순위</Table.Th>
                  <Table.Th>병원명 (코드)</Table.Th>
                  <Table.Th style={{ width: 120 }}>총 요청</Table.Th>
                  <Table.Th style={{ width: 120 }}>완료 건수</Table.Th>
                  <Table.Th style={{ width: 120 }}>대기 건수</Table.Th>
                  <Table.Th style={{ width: 220 }}>응답률</Table.Th>
                  <Table.Th style={{ width: 140 }}>평균 소요 시간</Table.Th>
                  <Table.Th style={{ width: 130 }}>최근 요청일</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.companies.map((company, index) => {
                  const rateColor =
                    company.responseRate >= 80
                      ? "teal"
                      : company.responseRate >= 50
                        ? "yellow"
                        : "red";

                  return (
                    <Table.Tr
                      key={company.companyId}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setSelectedCompanyId(
                          selectedCompanyId === company.companyId ? null : company.companyId,
                        )
                      }
                    >
                      <Table.Td>
                        <Badge
                          size="sm"
                          variant={index < 3 ? "filled" : "light"}
                          color={
                            index === 0
                              ? "yellow"
                              : index === 1
                                ? "gray"
                                : index === 2
                                  ? "orange"
                                  : "gray"
                          }
                        >
                          {index + 1}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Text fw={600} size="sm">
                            {company.companyName}
                          </Text>
                          <Badge size="xs" variant="outline" color="gray">
                            {company.companyCode}
                          </Badge>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" color="blue" size="md">
                          {company.totalRequests.toLocaleString()}건
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="teal" fw={600}>
                          {company.completedRequests.toLocaleString()}건
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="yellow.8" fw={500}>
                          {company.pendingRequests.toLocaleString()}건
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={4}>
                          <Group justify="space-between">
                            <Text size="xs" c="dimmed">
                              응답률
                            </Text>
                            <Text size="xs" fw={700} c={rateColor}>
                              {company.responseRate}%
                            </Text>
                          </Group>
                          <Progress
                            value={company.responseRate}
                            color={rateColor}
                            size="sm"
                            radius="xl"
                          />
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" fw={500} c="dimmed">
                          {formatMinutes(company.avgResponseTimeMinutes)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <Calendar size={13} color="gray" />
                          <Text size="xs" c="dimmed">
                            {company.lastRequestedAt || "-"}
                          </Text>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          ) : (
            <Paper p="xl" withBorder radius="md" style={{ textAlign: "center" }}>
              <Text c="dimmed" size="sm">
                조회된 병원별 요청 데이터가 없습니다.
              </Text>
            </Paper>
          )}
        </Card>
      </Stack>
    </TitleContainer>
  );
}
